import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import ContentTabs from "@/components/ContentTabs";
import InPlanBanner from "@/components/InPlanBanner";
import MarketingProgramSections from "@/components/MarketingProgramSections";
import CompoundPicker from "@/components/CompoundPicker";
import { FullPageLoader } from "@/components/Loader";
import { apiGet } from "@/lib/api";
import { useAssessment } from "@/lib/assessment";
import { useApplicationTray } from "@/lib/applicationTray";
import { C, metaForSlug, colorSchemeForProgram, categoryNumberForProgram } from "@/lib/brand";
import { getLocalProgramContent } from "@/lib/marketingContent";
import { fmtMoney } from "@/lib/format";

// When the backend program record has no rich `content.tabs` yet (typical
// for programs the operator has not finished authoring), we fall back to
// the local marketing JSON so the member-area info page still has Promise
// / Reality / How-It-Works / Who-It's-For / Voya9-Difference / FAQ blocks.
// Active-for-purchase programs already ship rich content tabs from the
// CMS, so the marketing JSON acts as a safety net rather than the source.
function mergeWithLocal(programFromApi, slug) {
  if (!programFromApi) return programFromApi;
  const local = getLocalProgramContent(slug);
  if (!local) return programFromApi;
  const merged = { ...programFromApi };
  // Editorial section blocks — only fill in if the API didn't ship them.
  ["promise", "reality", "howItWorks", "whoItsFor", "voya9Difference", "pricing", "faq"].forEach((k) => {
    if (merged[k] == null && local[k] != null) merged[k] = local[k];
  });
  // Backfill tagline/description from local copy when missing so inactive
  // programs (which currently ship empty tagline/description from the
  // backend) still read like real editorial pages.
  if (!merged.tagline && local.tagline) merged.tagline = local.tagline;
  if (!merged.description && local.description) merged.description = local.description;
  return merged;
}

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  // BE Phase A: program-aware compound + stage data. Fetched in parallel
  // with the legacy program record so we light up the new UI the moment
  // the BE rolls out, with a graceful fallback to the legacy TierGrid
  // for slugs the BE hasn't seeded yet (or environments where the
  // /compounds endpoint isn't deployed).
  const [compoundData, setCompoundData] = useState(null); // { compounds, decisionHelper } | null
  const detailPanelRef = useRef(null);
  const { update } = useAssessment();
  const { addOrReplace, remove, items: trayItems } = useApplicationTray();

  useEffect(() => {
    let cancelled = false;
    // Public compound endpoint per BE Phase A handoff. Returns 404 on
    // environments that haven't deployed yet — we silently fall back to
    // the legacy tier flow in that case.
    apiGet(`/programs/${slug}/compounds`, { auth: false })
      .then((res) => {
        if (cancelled) return;
        const compounds = Array.isArray(res?.compounds) ? res.compounds : [];
        if (compounds.length > 0) {
          setCompoundData({
            compounds,
            decisionHelper: res?.decisionHelper || null,
            // BE Phase D — sub-section list lives at the top of the
            // response, or nested under .program on some shapes. We
            // accept either so the FE works as soon as the BE ships.
            subSections: res?.subSections || res?.program?.subSections || null,
          });
        }
      })
      .catch(() => { /* legacy TierGrid stays in place */ });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    // The single-program endpoint returns 404 for programs the operator
    // has marked as inactive (`isActive=false`). Members are still allowed
    // to land on those slugs from /app/browse to read the editorial info,
    // so when the direct fetch fails we fall back to the consumer programs
    // list (which includes inactive entries) and pluck the slug we want.
    apiGet(`/consumer/programs/${slug}`, { auth: false })
      .then((p) => { if (!cancelled) setProgram(mergeWithLocal(p, slug)); })
      .catch(() =>
        apiGet("/consumer/programs")
          .then((list) => {
            if (cancelled) return;
            const hit = (Array.isArray(list) ? list : []).find(
              (p) => p?.slug === slug,
            );
            if (hit) setProgram(mergeWithLocal(hit, slug));
            else setNotFound(true);
          })
          .catch(() => { if (!cancelled) setNotFound(true); }),
      );
    return () => { cancelled = true; };
  }, [slug]);

  const meta = useMemo(() => (program ? metaForSlug(program.slug) : null), [program]);
  const scheme = useMemo(() => (program ? colorSchemeForProgram(program) : null), [program]);
  const categoryNum = useMemo(() => (program ? categoryNumberForProgram(program) : "01"), [program]);
  const brandColor = scheme?.background || program?.color || meta?.color || C.peach;
  const brandAccent = scheme?.textSecondary || program?.accentColor || meta?.dark || C.peachDeep;
  const heroTextPrimary = scheme?.textPrimary || brandAccent;

  if (notFound) {
    return (
      <div style={{ padding: 64, textAlign: "center" }} data-testid="program-not-found">
        <Headline before="NOT" italic="found." size={56} />
        <Link to="/app/programs" style={{ marginTop: 24, display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "0.18em" }}>← BACK TO BROWSE</Link>
      </div>
    );
  }
  if (!program) return <FullPageLoader />;

  const protocols = program.protocols || [];
  const programId = program._id || program.id || program.slug;
  // Operator-controlled "Active for purchase" flag. When false, this is
  // an editorial info page — no tier picker, no plan add — and the page
  // renders the marketing-style content tabs in the member shell.
  const isActiveForPurchase = program.isActive === true;

  // Spec §5: this program's tier in Plan derives from the tray, not from
  // local selection state. Sprint 2 collapses "select" + "add to plan"
  // into a single act — selection is light, just like a wishlist.
  const inPlanItem = trayItems.find((it) => it.programId === programId || it.programSlug === program.slug);
  const inPlanIndex = inPlanItem
    ? protocols.findIndex(
        (p) =>
          (p._id || p.id || p.tier) === inPlanItem.tierId ||
          p.tier === inPlanItem.tierKey,
      )
    : -1;

  // Tap on a tier card. State machine per spec §5:
  //   • not in plan → add to plan
  //   • this tier already in plan → remove (with confirmation)
  //   • different tier in plan → switch
  const onTierAction = (idx) => {
    const tier = protocols[idx];
    if (!tier) return;
    const isThisTierInPlan = inPlanIndex === idx;
    if (isThisTierInPlan) {
      if (window.confirm(`Remove ${program.name} from your plan? You can always add it back.`)) {
        remove(programId);
      }
      return;
    }
    addOrReplace({
      programId,
      programSlug: program.slug,
      programName: program.name,
      brandColor,
      tierId: tier._id || tier.id || tier.tier,
      tierKey: tier.tier,
      tierName: tier.tierLabel || tier.tier,
      monthlyPrice: tier.monthlyPrice,
    });
    // Keep the legacy assessment store populated for any consumer flows
    // still referencing these fields. The act is silent — the in-plan
    // banner above will show the new state immediately.
    update({
      programSlug: program.slug,
      selectedProtocolTier: tier.tier,
      selectedProductId: tier.productId,
      selectedProtocol: tier,
      monthlyPrice: tier.monthlyPrice,
    });
  };

  return (
    <div data-testid="program-detail-page" style={{ background: C.cream, paddingBottom: 120 }}>
      {/* Hero — color-tile treatment with italic serif numbered overlay.
          `overflow: hidden` is intentional on this section because the
          giant numeral that floats off the right edge needs to be
          clipped to the hero. We deliberately do NOT use overflow:
          hidden on the page root because that would break
          `position: sticky` on any descendant (compound CTA, etc.). */}
      <section style={{ background: brandColor, color: heroTextPrimary, padding: "72px 24px 88px", position: "relative", overflow: "hidden" }}>
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 28,
            right: 32,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "clamp(80px, 14vw, 160px)",
            lineHeight: 1,
            opacity: 0.5,
            color: scheme?.numberOverlay || brandAccent,
            pointerEvents: "none",
            letterSpacing: "-0.04em",
          }}
        >
          {categoryNum}
        </span>
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Link
            to="/app/browse"
            data-testid="back-to-programs"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: brandAccent,
              textDecoration: "none",
              marginBottom: 24,
              opacity: 0.75,
            }}
          >
            ← BROWSE PROGRAMS
          </Link>
          <Eyebrow color={brandAccent} testId="program-detail-category-eyebrow">
            CATEGORY {categoryNum}
          </Eyebrow>
          <h1 style={{ marginTop: 18, fontSize: "clamp(48px, 8vw, 84px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 0.95, textTransform: "uppercase", color: heroTextPrimary, maxWidth: "82%" }}>
            {(program.name || meta.t).toUpperCase()}
          </h1>
          {(program.tagline || meta.q) && (
            <p
              style={{
                marginTop: 22,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                fontSize: "clamp(20px, 2.4vw, 26px)",
                color: brandAccent,
                lineHeight: 1.4,
                maxWidth: 600,
              }}
            >
              "{program.tagline || meta.q}"
            </p>
          )}
          {program.description && (
            <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.6, maxWidth: 600, color: heroTextPrimary, opacity: 0.85 }}>
              {program.description}
            </p>
          )}
        </div>
      </section>

      {/* In-plan banner — appears when this program (or any program) is
          in the member's plan. Spec §5: persistent bridge between browse
          and the Plan tab. Hidden on info-only (not active for purchase)
          pages since they can't add to plan. */}
      {isActiveForPurchase && <InPlanBanner currentProgramSlug={program.slug} />}

      {/* What to expect — lifestyle timeline. Position depends on
          which picker we're rendering:
            • Legacy tier picker → ABOVE the picker (unchanged).
            • New compound picker → BELOW the picker, just before the
              Decision Helper, so the flow reads:
                cards → "PICK A path" → details → "WHAT TO EXPECT" → "LET'S walk you through it"
          We render here only for the legacy path; the compound path
          slots the same content into CompoundPicker via
          `beforeDecisionHelper`. */}
      {isActiveForPurchase && !(compoundData && compoundData.compounds.length > 0) && (
      <section style={{ padding: "84px 24px 64px", position: "relative", background: C.cream }}>
        {/* Wedge paints the previous section's program color so the
            diagonal boundary reads as the hero color cutting into cream. */}
        <TopWedge color={brandColor} dir="right" />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <WhatToExpectTimeline />
        </div>
      </section>
      )}

      {/* Picker — only shown when active for purchase. Otherwise we
          render a soft "not available yet" notice above the content tabs
          so members understand why there's no Add CTA.
          
          When BE Phase A compound data exists for this program we render
          the new compound + stage picker. Otherwise fall back to the
          legacy tier grid so today's flow keeps working until the BE
          rolls out the /compounds endpoint everywhere. */}
      {(isActiveForPurchase || compoundData) ? (
      <section style={{ padding: "32px 24px 32px", position: "relative" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          {compoundData && compoundData.compounds.length > 0 ? (
            <CompoundPicker
              compounds={compoundData.compounds}
              decisionHelper={compoundData.decisionHelper}
              subSections={compoundData.subSections}
              programColor={brandColor}
              programAccent={brandAccent}
              isActiveForPurchase={isActiveForPurchase}
              programName={program.name}
              programSlug={program.slug}
              inPlanItem={inPlanItem || null}
              beforeDecisionHelper={
                isActiveForPurchase ? (
                  <div style={{ marginTop: 56, marginBottom: 8 }}>
                    <WhatToExpectTimeline />
                  </div>
                ) : null
              }
              onAddCompound={(compound, stage) => {
                // For single-stage compounds the tier-name alias drops
                // the stage suffix to match BE projection behavior — the
                // member sees just the compound name (e.g. "Semaglutide")
                // both in the tray and on Plan / dashboard.
                const isSingleStage = compound.singleStage === true || (compound.stages || []).length <= 1;
                const tierKey = `${compound.slug}:${stage.stageKey}`;
                const tierName = isSingleStage
                  ? compound.name
                  : `${compound.name} · ${stage.stageLabel}`;
                addOrReplace({
                  programId,
                  programSlug: program.slug,
                  programName: program.name,
                  brandColor,
                  tierId: stage.productId || stage.product?.id || tierKey,
                  tierKey,
                  tierName,
                  monthlyPrice: stage.monthlyPrice,
                });
                // Phase B clean-cut — only canonical compound + stage
                // fields go into the assessment context. The BE no
                // longer accepts selectedProductId / selectedProtocolTier
                // on consultation submission.
                update({
                  programSlug: program.slug,
                  selectedCompoundId: compound.compoundId,
                  selectedCompoundSlug: compound.slug,
                  selectedCompoundName: compound.name,
                  selectedStage: stage.stageKey,
                  selectedStageLabel: isSingleStage ? null : stage.stageLabel,
                  monthlyPrice: stage.monthlyPrice,
                  // Clear any legacy values in case the member is
                  // rehydrating an in-flight session that was started
                  // before the Phase B deploy.
                  selectedProtocolTier: null,
                  selectedProductId: null,
                  selectedProtocol: null,
                });
                // After adding, scroll the member back to the program
                // header so they see the InPlanBanner (★ IN YOUR PLAN
                // + VIEW PLAN / CONTINUE BROWSING). Without this, the
                // page stays anchored deep inside the stage detail and
                // members miss the confirmation + next-step affordance.
                requestAnimationFrame(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                });
              }}
              onRemoveCompound={() => {
                // Reuse the existing confirm pattern for parity with the
                // legacy tier remove flow.
                if (window.confirm(`Remove ${program.name} from your plan? You can always add it back.`)) {
                  remove(programId);
                }
              }}
            />
          ) : (
            <>
              <Eyebrow color={brandAccent}>★ CHOOSE YOUR PROTOCOL</Eyebrow>
              <Headline before="PICK A" italic="tier." size={48} className="mt-4" />

              {program.tierDisclosure && (
                <p
                  data-testid="tier-disclosure"
                  style={{
                    marginTop: 14,
                    maxWidth: 640,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: C.warmGray,
                    fontFamily: '"Instrument Serif", Georgia, serif',
                    fontStyle: "italic",
                  }}
                >
                  {program.tierDisclosure}
                </p>
              )}

              {protocols.length === 0 && (
                <p style={{ marginTop: 24, fontSize: 14, color: C.warmGray }}>Protocols will be visible once your physician reviews your assessment.</p>
              )}

              <TierGrid
                protocols={protocols}
                inPlanIndex={inPlanIndex}
                expandedIndex={expandedIndex}
                onTierAction={onTierAction}
                onLearnMore={(i) => {
                  const next = expandedIndex === i ? null : i;
                  setExpandedIndex(next);
                  if (next != null) {
                    requestAnimationFrame(() => {
                      detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                  }
                }}
                programColor={brandColor}
                programAccent={brandAccent}
                tierFinePrint={program.tierFinePrint}
              />

              {/* Shared expanded-detail panel with tier-switcher pills.
                  Rendered ONCE below the grid (not per-card) so members can
                  flip between tiers without scrolling back up to a different card. */}
              <TierDetailPanel
                ref={detailPanelRef}
                protocols={protocols}
                expandedIndex={expandedIndex}
                onSwitch={setExpandedIndex}
                onClose={() => setExpandedIndex(null)}
                onTierAction={onTierAction}
                inPlanIndex={inPlanIndex}
                programColor={brandColor}
                programAccent={brandAccent}
              />
            </>
          )}

          {/* What's included (program-level) */}
          <div style={{ marginTop: 40, padding: 28, background: C.bone }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>★ EVERY PLAN INCLUDES</div>
            <ul style={{ marginTop: 14, paddingLeft: 20, fontSize: 14, lineHeight: 1.75, color: C.ink }}>
              <li>Licensed physician review of your case</li>
              <li>Personalized monthly protocol</li>
              <li>Discreet shipping included</li>
              <li>Monthly check-ins & care team messaging</li>
              <li>Every batch independently lab-tested and physician-verified</li>
              <li>Pause, skip, or cancel any time</li>
            </ul>
          </div>
        </div>
      </section>
      ) : null}

      {/* Marketing-style editorial sections — surfaced inside the member
          shell only for not-yet-available programs, so members get the
          same depth of "more info" they'd see on the public site without
          ever leaving /app/*. Active programs rely on the richer
          ContentTabs block below instead. */}
      {!isActiveForPurchase && <MarketingProgramSections program={program} />}

      {/* Program content tabs */}
      {program.content?.tabs?.length > 0 && (
        <section style={{ padding: "32px 24px 64px", position: "relative", background: C.cream }}>
          <TopWedge color={C.cream} dir="left" />
          <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <Eyebrow color={brandAccent}>★ LEARN MORE</Eyebrow>
            <Headline before="ABOUT THIS" italic="program." size={48} className="mt-4" />
            <div style={{ marginTop: 36 }}>
              <ContentTabs tabs={program.content.tabs} programColor={brandAccent} testIdPrefix="program-content" />
            </div>
          </div>
        </section>
      )}

      {/* Final CTA — points to Plan, not "Review & Begin." Per spec §5,
          selected programs collect in Plan; payment happens there. When
          the tray is empty, this section reduces to a soft "Browse other
          programs" link instead of a full-bleed call-to-action. Skipped
          entirely on info-only pages — those have a back-to-browse link
          in the hero and shouldn't push toward Plan since nothing's been
          added. */}
      {isActiveForPurchase ? (
        <FinalCta
          hasSelections={trayItems.length > 0}
          brandColor={brandColor}
          brandAccent={brandAccent}
          heroTextPrimary={heroTextPrimary}
        />
      ) : (
        <section
          data-testid="program-info-back-cta"
          style={{ padding: "32px 24px 64px", background: C.cream, textAlign: "center" }}
        >
          <Link
            to="/app/browse"
            style={{
              display: "inline-block",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.22em",
              color: C.ink,
              textDecoration: "none",
              borderBottom: `2px solid ${C.ink}`,
              paddingBottom: 4,
            }}
          >
            ← BACK TO BROWSE
          </Link>
        </section>
      )}
    </div>
  );
}

function WhatToExpectTimeline() {
  return (
    <>
      <Eyebrow>★ WHAT TO EXPECT</Eyebrow>
      <Headline before="A LIFESTYLE" italic="not a quick fix." size={48} className="mt-4" />
      <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }} className="!grid-cols-1 md:!grid-cols-3">
        {[
          { n: "WEEK 1–2", t: "Onboarding", s: "Get matched, set up your plan, first shipment ships." },
          { n: "MONTH 1–2", t: "Adjustment", s: "Initial response — your physician adjusts as needed." },
          { n: "MONTH 3+", t: "Sustainment", s: "Settle into the rhythm. Optional protocol tweaks." },
        ].map((s) => (
          <div key={s.n} data-testid={`expect-${s.n}`} style={{ padding: 24, background: C.bone }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.warmGray }}>{s.n}</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</div>
            <div style={{ marginTop: 8, fontSize: 14, color: C.warmGray, lineHeight: 1.55 }}>{s.s}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function FinalCta({ hasSelections, brandColor, brandAccent, heroTextPrimary }) {
  if (!hasSelections) {
    return (
      <section
        data-testid="program-cta-empty"
        style={{ padding: "64px 24px", background: C.cream, textAlign: "center" }}
      >
        <Link
          to="/app/browse"
          style={{
            display: "inline-block",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: C.ink,
            textDecoration: "none",
            borderBottom: `2px solid ${C.ink}`,
            paddingBottom: 4,
          }}
        >
          BROWSE OTHER PROGRAMS →
        </Link>
      </section>
    );
  }
  return (
    <section
      data-testid="program-cta-final"
      style={{
        background: brandColor,
        color: heroTextPrimary,
        padding: "84px 24px 96px",
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <Eyebrow color={brandAccent}>★ READY TO MOVE FORWARD?</Eyebrow>
        <h2
          style={{
            marginTop: 18,
            fontSize: "clamp(36px, 6vw, 60px)",
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.0,
            textTransform: "uppercase",
            color: heroTextPrimary,
          }}
        >
          YOUR PLAN
          <br />
          <span
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontWeight: 400,
              textTransform: "none",
              color: brandAccent,
            }}
          >
            is waiting.
          </span>
        </h2>
        <p
          style={{
            marginTop: 22,
            fontSize: 16,
            lineHeight: 1.6,
            color: heroTextPrimary,
            opacity: 0.9,
            maxWidth: 540,
            margin: "22px auto 0",
          }}
        >
          Selected programs collect in your plan. Pay one review fee for all of them and begin your assessment when you're ready.
        </p>
        <div style={{ marginTop: 32 }}>
          <Link to="/app/plan" data-testid="program-cta-view-plan" style={{ textDecoration: "none" }}>
            <Button variant="primary" size="lg">VIEW MY PLAN →</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ----- Tier grid with progressive disclosure (primary + secondary) ----- */
function TierGrid({ protocols, inPlanIndex, expandedIndex, onTierAction, onLearnMore, programColor, programAccent, tierFinePrint }) {
  const [showSecondary, setShowSecondary] = useState(false);

  // Position-driven split. The admin "Protocols" tab already controls
  // display order via drag-to-reorder (sortOrder on each protocol record),
  // so we honor that as the single source of truth:
  //   • Top 3 by sortOrder → always-visible "Pick a tier" row.
  //   • Position #4 and beyond → tucked under "Compare more options".
  // No frontend overrides — if an admin wants to demote the 3rd tier or
  // promote the 4th, they reorder them in the admin UI.
  const ordered = protocols
    .map((p, i) => ({ p, i, sort: p.sortOrder ?? i + 1 }))
    .sort((a, b) => a.sort - b.sort);

  const primary = ordered.slice(0, 3);
  const secondary = ordered.slice(3);

  const hasSecondary = secondary.length > 0;
  const secondaryOriginalIndices = new Set(secondary.map(({ i }) => i));
  const selectedIsHiddenSecondary =
    hasSecondary &&
    !showSecondary &&
    inPlanIndex >= 0 &&
    secondaryOriginalIndices.has(inPlanIndex);

  const renderRow = (rows) => {
    const cols = rows.length === 1 ? 1 : Math.min(rows.length, 3);
    return (
      <div
        style={{
          marginTop: 32,
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 16,
          maxWidth: cols === 1 ? 460 : "none",
          marginLeft: cols === 1 ? "auto" : undefined,
          marginRight: cols === 1 ? "auto" : undefined,
        }}
        className={cols > 1 ? "!grid-cols-1 md:!grid-cols-3" : ""}
      >
        {rows.map(({ p, i }) => (
          <TierCardWithExpand
            key={(p.tier || "") + (p.productId || i)}
            protocol={p}
            index={i}
            inPlanIndex={inPlanIndex}
            isExpanded={expandedIndex === i}
            onTierAction={() => onTierAction(i)}
            onLearnMore={() => onLearnMore(i)}
            programColor={programColor}
            programAccent={programAccent}
            tierFinePrint={tierFinePrint}
          />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="tier-grid">
      {renderRow(primary)}

      {hasSecondary && (
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <button
            type="button"
            data-testid="compare-more-options-btn"
            onClick={() => setShowSecondary(!showSecondary)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 26px",
              background: showSecondary ? C.ink : "transparent",
              color: showSecondary ? C.cream : C.ink,
              border: `2px solid ${C.ink}`,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              cursor: "pointer",
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            {showSecondary ? "HIDE OPTIONS" : "COMPARE MORE OPTIONS"}
            <span
              style={{
                display: "inline-block",
                transform: showSecondary ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms ease",
              }}
            >
              ↓
            </span>
          </button>
          <p
            style={{
              marginTop: 12,
              fontSize: 12,
              color: C.warmGray,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
            }}
          >
            {secondary.length} additional {secondary.length === 1 ? "tier" : "tiers"} available
          </p>
        </div>
      )}

      {selectedIsHiddenSecondary && (
        <div
          data-testid="hidden-selection-banner"
          style={{
            marginTop: 16,
            padding: "12px 14px",
            background: C.bone,
            borderLeft: `4px solid ${C.peachDeep}`,
            fontSize: 13,
            color: C.ink,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>
            <strong>Currently selected:</strong> {protocols[inPlanIndex].tier} tier
          </span>
          <button
            type="button"
            onClick={() => setShowSecondary(true)}
            style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.peachDark, textDecoration: "underline" }}
          >
            SHOW
          </button>
        </div>
      )}

      {hasSecondary && showSecondary && (
        <div className="animate-fadeIn" style={{ marginTop: 32 }} data-testid="secondary-tier-section">
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.25em",
              color: C.warmGray,
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            ── ADDITIONAL OPTIONS ──
          </div>
          {renderRow(secondary)}
        </div>
      )}
    </div>
  );
}

/* ----- Rich tier card with inline learn-more expansion ----- */
function TierCardWithExpand({
  protocol,
  index,
  inPlanIndex,
  isExpanded,
  onTierAction,
  onLearnMore,
  programColor,
  programAccent,
  tierFinePrint,
}) {
  const visibleItems = (protocol.bundleItems || []).filter((b) => b && b.isVisible !== false);
  const hasDosing = !!protocol.dosing && (protocol.dosing.frequency || protocol.dosing.supplyDuration);
  const hasContent = !!protocol.content?.tabs?.length;

  // Spec §5 — three-state action per tier card.
  const isInPlan = inPlanIndex === index;
  const otherTierInPlan = inPlanIndex >= 0 && inPlanIndex !== index;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%", minWidth: 0, width: "100%" }}>
      <div
        data-testid={`tier-card-${(protocol.tier || "").toLowerCase()}`}
        style={{
          background: C.pure,
          border: `2px solid ${isInPlan ? C.ink : C.warmGray3}`,
          transition: "border-color 180ms ease, box-shadow 180ms ease",
          boxShadow: isInPlan ? "0 8px 24px rgba(15,14,13,0.08)" : "none",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* "✓ IN YOUR PLAN" callout — replaces tierBadge when this tier
            is in plan, per spec §5. */}
        {isInPlan ? (
          <div
            data-testid={`tier-in-plan-${index}`}
            style={{
              padding: "10px 20px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: C.cream,
              background: C.ink,
              textTransform: "uppercase",
            }}
          >
            ✓ IN YOUR PLAN
          </div>
        ) : protocol.tierBadge && (
          <div
            data-testid={`tier-badge-${index}`}
            style={{
              padding: "10px 20px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: C.ink,
              background: programColor,
              textTransform: "uppercase",
            }}
          >
            ★ {protocol.tierBadge}
          </div>
        )}

        <div style={{ padding: "28px 24px", flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: programAccent }}>
            {(protocol.tierLabel || protocol.tier || "").toUpperCase()}
          </div>
          <h3 style={{ marginTop: 8, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2, color: C.ink }}>
            {protocol.compoundDescription || `${protocol.tier || "Tier"} protocol`}
          </h3>

          <div style={{ marginTop: 16, marginBottom: 4 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
              {fmtMoney(protocol.monthlyPrice)}
            </span>
            <span style={{ fontSize: 13, color: C.warmGray, marginLeft: 4 }}>/mo</span>
          </div>

          {tierFinePrint && (
            <p
              data-testid={`tier-fineprint-${index}`}
              style={{
                marginTop: 4,
                marginBottom: 0,
                fontSize: 12,
                color: C.warmGray,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                lineHeight: 1.4,
              }}
            >
              {tierFinePrint}
            </p>
          )}

          {protocol.bestFor && (
            <p
              data-testid={`tier-bestfor-${index}`}
              style={{
                marginTop: 14,
                fontSize: 14,
                color: C.warmGray,
                lineHeight: 1.55,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
              }}
            >
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontStyle: "normal", fontWeight: 700, color: C.ink }}>
                Best for:{" "}
              </span>
              {protocol.bestFor}
            </p>
          )}

          {visibleItems.length > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${C.warmGray3}`, margin: "22px 0 18px" }} />
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
                WHAT'S IN YOUR BOX
              </div>
              <ul data-testid={`tier-bundle-${index}`} style={{ margin: "14px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                {visibleItems.map((it, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: C.ink, lineHeight: 1.45 }}>
                    <span style={{ color: programAccent, fontWeight: 800 }}>•</span>
                    <span>
                      {it.displayName}
                      {it.quantity > 1 && (
                        <span style={{ color: C.warmGray, marginLeft: 4 }}>({it.quantity})</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {hasDosing && (
            <>
              <div style={{ borderTop: `1px solid ${C.warmGray3}`, margin: "22px 0 18px" }} />
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>DOSING</div>
              {protocol.dosing.frequency && (
                <div data-testid={`tier-dosing-freq-${index}`} style={{ marginTop: 10, fontSize: 14, color: C.ink }}>
                  {protocol.dosing.frequency}
                </div>
              )}
              {protocol.dosing.supplyDuration && (
                <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
                  {protocol.dosing.supplyDuration}
                </div>
              )}
              {(protocol.dosing.schedule || protocol.dosing.administration) && (
                <div style={{ marginTop: 8, fontSize: 12, color: C.warmGray, lineHeight: 1.5 }}>
                  {protocol.dosing.schedule}
                  {protocol.dosing.schedule && protocol.dosing.administration ? " · " : ""}
                  {protocol.dosing.administration}
                </div>
              )}
            </>
          )}

          <div style={{ flex: 1 }} />

          {/* One action per tier card per spec §5:
                in plan → REMOVE FROM PLAN (smaller, secondary)
                other tier in plan → SWITCH TO THIS TIER (peach outline)
                nothing in plan → SELECT THIS TIER (primary peach) */}
          <button
            type="button"
            data-testid={`tier-action-${index}`}
            onClick={onTierAction}
            style={{
              marginTop: 24,
              width: "100%",
              padding: isInPlan ? "12px 22px" : "16px 22px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              background: isInPlan ? "transparent" : (otherTierInPlan ? "transparent" : programColor),
              color: isInPlan ? C.ink : C.ink,
              border: `1.5px solid ${C.ink}`,
              cursor: "pointer",
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            {isInPlan
              ? "✗ REMOVE FROM PLAN"
              : otherTierInPlan
                ? "SWITCH TO THIS TIER →"
                : "SELECT THIS TIER →"}
          </button>

          {hasContent && (
            <button
              type="button"
              data-testid={`tier-learn-more-${index}`}
              onClick={onLearnMore}
              style={{
                marginTop: 10,
                width: "100%",
                padding: "10px 0",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                color: C.warmGray,
                textTransform: "uppercase",
                background: "transparent",
              }}
            >
              {isExpanded ? "Viewing details ↓" : "Learn more ↓"}
            </button>
          )}
        </div>
      </div>

      {/* Inline expansion removed — shared TierDetailPanel below the grid
          handles the rich content with a tier-switcher pill row. */}
    </div>
  );
}

/* ----- Shared, switchable tier detail panel (lives below the grid) ----- */

const TierDetailPanel = forwardRef(function TierDetailPanel(
  { protocols, expandedIndex, onSwitch, onClose, onTierAction, inPlanIndex, programColor, programAccent },
  ref,
) {
  if (expandedIndex == null) return null;
  const protocol = protocols[expandedIndex];
  if (!protocol) return null;
  const tabs = protocol.content?.tabs || [];

  return (
    <div
      ref={ref}
      data-testid="tier-detail-panel"
      className="animate-fadeIn"
      style={{
        marginTop: 12,
        background: C.bone,
        border: `1px solid ${C.warmGray3}`,
        // App header (~57) + X strip (~32) + pill row (~52) + breath = 160
        scrollMarginTop: 160,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Two-row header: X strip on top with the close button anchored to the
          panel's top-right corner; pill row spans the full width below.
          NOT sticky — sticky was creating a ghost gap inside the panel as
          users scrolled. The panel scrolls naturally; the user can always
          scroll back up to switch tiers. */}
      <div
        data-testid="tier-detail-switcher"
        style={{
          background: C.bone,
          borderBottom: `1px solid ${C.warmGray3}`,
        }}
      >
        {/* Close strip — X sits flush at the top edge of the panel.
            Tight 6px padding all around keeps it small but tappable. */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 6px 0" }}>
          <button
            type="button"
            data-testid="tier-detail-close"
            onClick={onClose}
            aria-label="Close details"
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: "transparent",
              border: `1.5px solid ${C.warmGray3}`,
              fontSize: 12,
              fontWeight: 700,
              color: C.warmGray,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        {/* Pill row — full horizontal width, tight to the X strip above */}
        <div
          className="no-scrollbar"
          style={{ display: "flex", gap: 8, overflowX: "auto", padding: "6px 12px 12px", minWidth: 0 }}
        >
          {protocols.map((p, i) => {
            const on = i === expandedIndex;
            const label = (p.tierLabel || p.tier || `Tier ${i + 1}`).toUpperCase();
            return (
              <button
                key={(p.tier || "") + i}
                type="button"
                data-testid={`tier-pill-${i}`}
                onClick={() => {
                  onSwitch(i);
                  requestAnimationFrame(() => {
                    if (ref && ref.current) {
                      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  });
                }}
                style={{
                  flexShrink: 0,
                  padding: "8px 14px",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: on ? C.cream : C.ink,
                  background: on ? C.ink : "transparent",
                  border: `1.5px solid ${C.ink}`,
                  borderRadius: 999,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background 160ms ease, color 160ms ease",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "40px 22px 24px", minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: programAccent, marginBottom: 6 }}>
          {(protocol.tierLabel || protocol.tier || "").toUpperCase()}
        </div>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2, color: C.ink }}>
          {protocol.compoundDescription || `${protocol.tier || "Tier"} protocol`}
        </h3>
        <div style={{ marginTop: 10, fontSize: 14, color: C.warmGray }}>
          {fmtMoney(protocol.monthlyPrice)} <span style={{ opacity: 0.7 }}>/mo</span>
        </div>

        {tabs.length > 0 ? (
          <div style={{ marginTop: 24 }}>
            <ContentTabs tabs={tabs} programColor={programAccent} testIdPrefix={`tier-${expandedIndex}-content`} />
          </div>
        ) : (
          <p style={{ marginTop: 18, fontSize: 14, color: C.warmGray, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif' }}>
            More information about this tier will be available soon.
          </p>
        )}

        {/* Quick action — same three-state behavior as the tier card,
            so members can act without scrolling back up. */}
        <TierDetailAction
          isInPlan={inPlanIndex === expandedIndex}
          otherTierInPlan={inPlanIndex >= 0 && inPlanIndex !== expandedIndex}
          programColor={programColor}
          onClick={() => {
            onTierAction(expandedIndex);
            // Close the panel after a successful add/switch so the
            // member sees the in-plan banner update at the top of the
            // page. For removal, leave it open so they can re-add easily.
            if (inPlanIndex !== expandedIndex) onClose();
          }}
        />
      </div>
    </div>
  );
});

function TierDetailAction({ isInPlan, otherTierInPlan, programColor, onClick }) {
  return (
    <button
      type="button"
      data-testid="tier-detail-action"
      onClick={onClick}
      style={{
        marginTop: 28,
        width: "100%",
        padding: isInPlan ? "12px 22px" : "16px 22px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        background: isInPlan ? "transparent" : (otherTierInPlan ? "transparent" : programColor),
        color: C.ink,
        border: `1.5px solid ${C.ink}`,
        cursor: "pointer",
      }}
    >
      {isInPlan
        ? "✗ REMOVE FROM PLAN"
        : otherTierInPlan
          ? "SWITCH TO THIS TIER →"
          : "SELECT THIS TIER →"}
    </button>
  );
}
